import { Close } from '@carbon/icons-react';
import { Pad, PadBox } from 'component/basics';
import { ActionButton } from 'component/button';
import { colorTextPrimary, colorTextSecondary } from 'component/color';
import type { Comment } from 'component/comment';
import { UtilityText, WebLinkTextButton } from 'component/text';
import { useTranslation } from 'component/translation';
import { useEffect } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import { useArticleInfo } from 'structure/article';
import type { QuestionConfig } from 'structure/question';
import { useDatastore, useGlobalParams, useModuleObject } from 'util/datastore';
import { logEventAsync } from 'util/eventlog';
import { defineModuleMap } from 'util/moduledata';
import type { Feature } from 'util/stdtypes';
import { t, undefF } from 'util/stdtypes';
import { urlToKey } from 'util/util';

// Extended comment type for entry points functionality
export type CommentWithEntryPoints = Comment & {
    entryUrl?: string;
    entryPoints?: Array<{ url: string }>;
    hideEntryPoint?: boolean;
};
export const ContextEntryPointsFeature: Feature<QuestionConfig, {}> = {
    name: 'Context Entry Points',
    key: 'context_entry_points',
    providesConfig: {
        composerTopWidgets: [ComposerEntryPointCard],
        commentMiddleWidgets: [PostedCommentEntryPointCard],
    },
};

export type ComposerEntryPointCardProps = { comment: CommentWithEntryPoints; setComment: (comment: CommentWithEntryPoints) => void };

export function ComposerEntryPointCard({ comment, setComment }: ComposerEntryPointCardProps) {
    const { entryUrl } = useGlobalParams();
    if (comment.hideEntryPoint || !entryUrl) {
        return null;
    }

    function onClose() {
        setComment({ ...comment, hideEntryPoint: true });
    }

    return (
        <PadBox bottom={40}>
            <ContextEntryPointCard entryUrl={entryUrl} onClose={onClose} />
        </PadBox>
    );
}

export type PostedCommentEntryPointCardProps = { comment: CommentWithEntryPoints };

export function PostedCommentEntryPointCard({ comment }: PostedCommentEntryPointCardProps) {
    const entryUrl = comment.entryUrl || comment.entryPoints?.[0].url;
    if (!entryUrl || comment.hideEntryPoint) return null;

    return (
        <PadBox top={16}>
            <ContextEntryPointCard entryUrl={entryUrl} />
        </PadBox>
    );
}

function ContextEntryPointCard({ entryUrl, onClose = undefF }) {
    const s = ContextEntryPointCardStyle;
    const articleInfo = useArticleInfo(entryUrl);
    const ariaLabel = useTranslation('Joined from') + ' ' + articleInfo?.title;
    const datastore = useDatastore();

    if (!entryUrl || !articleInfo || articleInfo.missing) return null;

    const onLinkPress = () => {
        logEventAsync(datastore, 'context_entry_point_click', { entryUrl });
    };

    return (
        <View>
            <Pad size={onClose ? 16 : 8} />
            <View aria-label={ariaLabel} style={s.outerBox}>
                <Image aria-hidden source={{ uri: articleInfo?.image }} style={s.image} />
                <Pad size={8} />
                <View style={s.textContainer}>
                    <UtilityText color={colorTextSecondary} label={t('Joined from')} type='tiny' weight='regular' />
                    <WebLinkTextButton
                        numberOfLines={1}
                        onLinkPress={onLinkPress}
                        text={articleInfo?.title}
                        type='small'
                        url={entryUrl}
                        weight='medium'
                    />
                </View>
                <Pad size={8} />
                {onClose && (
                    <ActionButton ariaLabel={t('Do not associate the source article with your response')} icon={Close} onPress={onClose} />
                )}
            </View>
        </View>
    );
}
const ContextEntryPointCardStyle = StyleSheet.create({
    outerBox: {
        width: '100%',
        borderRadius: 8,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: 36,
    },
    image: {
        height: 36,
        width: 36,
        borderRadius: 8,
    },
    textContainer: {
        flex: 1,
    },
});
